<template>
  <v-app v-bind:style="{ background: $vuetify.theme.themes.light.background }">
    <!--
      Navigation left
    -->
    <v-navigation-drawer dark v-model="drawer" app v-if="!$store.state.server.serverFunctionsLoading">
      <!-- ART-DECOR icon -->
      <v-card height="48" class="white--text">
        <v-card-text class="pt-0">
          <v-row dense align-content="center">
            <v-col class="py-0" cols="4" style="margin-top: 10px">
              <v-img
                width="69"
                :src="require('../src/assets/topmenu-ad-logo2.png')"
              ></v-img>
            </v-col>
            <v-col class="py-0 pl-2 pt-4" cols="8">
              <span class="font-weight-bold">{{ $root.apptitle }} {{ majminversion }}</span>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      <v-divider></v-divider>
      <v-card class="mx-auto" width="350">
        <v-list v-if="projectSelected === null || projectInFocus === null">
          <!-- The Home menu -->
          <v-list-item link :to="{ name: 'home' }">
            <v-list-item-icon>
              <v-icon>mdi-home</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ $t("home") }}</v-list-item-title>
          </v-list-item>
          <!-- Menu items if no project is selected -->
          <v-list-group
            v-for="(group, i) in menuItemsNoProjectSelected"
            :key="`noproject-${i}`"
            :prepend-icon="group.icon"
            :group="group.name"
          >
            <template v-slot:activator>
              <v-list-item-title>{{ $t(group.title) }}</v-list-item-title>
            </template>
            <v-list-item
              v-for="(item, j) in group.children"
              class="ml-6"
              :key="`noproject-${j}`"
              link
              :to="{ name: item.name }"
            >
              <v-list-item-title>{{ $t(item.title) }}</v-list-item-title>
            </v-list-item>
          </v-list-group>
          <!-- server admin menu if accessible -->
          <v-list-group
            v-for="(group, i) in menuItemsServerAdmin"
            :key="`serveradmin-${i}`"
            :prepend-icon="group.icon"
            :group="group.name"
          >
            <template v-slot:activator>
              <v-list-item-title>{{ $t(group.title) }}</v-list-item-title>
            </template>
            <v-list-item
              v-for="(item, j) in group.children"
              class="ml-6"
              :key="j"
              link
              :to="{ name: item.name }"
            >
              <v-list-item-title>{{ $t(item.title) }}</v-list-item-title>
            </v-list-item>
          </v-list-group>
        </v-list>
        <v-list v-else>
          <!-- The Home menu -->
          <v-list-item link :to="{ name: 'home' }">
            <v-list-item-icon>
              <v-icon>mdi-home</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ $t("home") }}</v-list-item-title>
          </v-list-item>
          <!-- Menu if a project is selected -->
          <v-list-group
            v-for="(group, i) in menuItemsIfProjectSelected"
            :key="`default-group-${i}`"
            :prepend-icon="group.icon"
            :group="`/${projectInFocus.prefix}/${group.name}/`"
          >
            <template v-slot:activator>
              <v-list-item-title>{{ $t(group.title) }}</v-list-item-title>
            </template>
            <v-list-item
              v-for="(item, j) in group.children"
              class="ml-6"
              :key="`default-${j}`"
              link
              :to="{ name: item.name, params: { prefix: projectInFocus.prefix} }"
            >
              <v-list-item-title>{{ $t(item.title) }}</v-list-item-title>
            </v-list-item>
          </v-list-group>
          <!-- server admin menu if accessible -->
          <v-list-group
            v-for="(group, i) in menuItemsServerAdmin"
            :key="`user-${i}`"
            :prepend-icon="group.icon"
            :group="group.name"
          >
            <template v-slot:activator>
              <v-list-item-title>{{ $t(group.title) }}</v-list-item-title>
            </template>
            <v-list-item
              v-for="(item, j) in group.children"
              class="ml-6"
              :key="`user-${j}`"
              link
              :to="{ name: item.name }"
            >
              <v-list-item-title>{{ $t(item.title) }}</v-list-item-title>
            </v-list-item>
          </v-list-group>
          <template v-if="$feature('showDesignMenuItems')">
            <v-list-group
              v-for="(group, i) in menuItemsDesign"
              :key="i"
              :prepend-icon="group.icon"
            >
              <template v-slot:activator>
                <v-list-item-title>{{ $t(group.title) }}</v-list-item-title>
              </template>
              <v-list-item
                v-for="(item, j) in group.children"
                class="ml-6"
                :key="j"
                link
                :to="{ name: item.name }"
              >
                <v-list-item-title>{{ $t(item.title) }}</v-list-item-title>
              </v-list-item>
            </v-list-group>
          </template>
        </v-list>
      </v-card>
    </v-navigation-drawer>
    <!--
      App bar Top Menu
    -->
    <v-app-bar dense app :color="$vuetify.theme.themes.light.topbottomstrip">
      <v-app-bar-nav-icon @click.stop="drawer = !drawer" />
      <template v-if="this.$router.currentRoute.path.includes('server-admin')">
        <!-- A special handliong for all admin panels -->
        <v-icon color="orange darken-1" class="pr-2">mdi-server-network</v-icon>
        <v-toolbar-title class="grey--text">
          {{ $t("server-admin") }}
          <span class="pl-7" v-if="userIsDBA"> DBA </span>
          <span class="pl-7" v-else style="color: red"> Non-DBA </span>
        </v-toolbar-title>
      </template>
      <template v-else>
        <!-- non admin panels are prepared to show details for a possibly selected project -->
        <v-menu
          v-if="projectSelected && projectLanguage && projectLocales.length > 1"
          offset-y
        >
          <!-- Project Language switch -->
          <template v-slot:activator="{ on, attrs }">
            <v-tooltip content-class="d-block" bottom>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn text v-bind="attrs" v-on="{...on, ...tooltip}" class="toolbar-btn mr-4 mr-sm-0">
                  <div style="width: 24px; float: left">
                    {{ projectLanguage.substring(0, 2) }}
                  </div>
                  <flag
                    :squared="Boolean(0)"
                    :iso="projectLanguage.substring(3, 5)"
                  ></flag>
                </v-btn>
              </template>
              <span>{{ $t('project-languages')}}</span>
            </v-tooltip>
          </template>
          <v-list>
            <v-list-item
              v-for="(item, index) in projectLocales"
              :key="index"
              @click="setProjectLanguage(item)"
            >
              <div style="width: 24px; float: left">
                {{ item.substring(0, 2) }}
              </div>
              <flag :squared="Boolean(0)" :iso="item.substring(3, 5)"></flag>
            </v-list-item>
          </v-list>
        </v-menu>
        <!-- Project icon if one is selected -->
        <v-icon v-if="projectSelected" color="orange darken-1" class="pr-2"
          >mdi-briefcase</v-icon
        >
        <!-- Project picker or title -->
        <ProjectPicker v-if="projectInFocus"/>
        <v-toolbar-title v-else class="grey--text">{{ $t("art-decor") }}®</v-toolbar-title>
      </template>
      <!-- display the message snackbar here for all modules who wnat to ignite a notify alert -->
      <MessageSnackbar ref="notify"/>
      <!-- display an alert / confirm dialog box here for all modules -->
      <AdAlertConfirm ref="alert"/>
      <AdAlertConfirm ref="confirm"/>
      <v-spacer></v-spacer>
      <!-- the normal login button, showed if not logged in and no login dialog is shown -->
      <AdButton
        v-if="!isUserLoggedIn"
        :text="$vuetify.breakpoint.xs ? ' ' : $t('login')"
        icon="mdi-login"
        @click="login"
        color="orange darken-3"
        :disabled="loginDialog"
        :noMarginRight="$vuetify.breakpoint.xs"
        :larger="$vuetify.breakpoint.xs"
      />
      <v-dialog v-model="loginDialog" width="500">
        <!-- the normal login dialog -->
        <v-card>
          <v-form ref="loginForm" v-model="loginValid" @submit.prevent="validate" persistent hide-overlay>
            <AdCardTitle dialog>{{ $t("login") }}</AdCardTitle>
            <v-card-text class="pa-7">
              <v-row dense>
                <v-col>
                  <v-text-field
                    v-model="username"
                    :label="$t('username')"
                    :rules="[v => !!v || $t('required-field')]"
                    :autofocus="!username"
                    autocomplete="username"
                    :disabled="authenticateOnly"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col>
                  <v-text-field
                    :autofocus="!!username"
                    v-model="password"
                    :append-icon="show1 ? 'mdi-eye' : 'mdi-eye-off'"
                    :type="show1 ? 'text' : 'password'"
                    name="input-10-1"
                    :label="$t('password')"
                    counter
                    @click:append="show1 = !show1"
                    :rules="rulesUser.password"
                    autocomplete="current-password"
                  />
                </v-col>
              </v-row>
              <v-row dense>
                <v-col>
                  <div v-if="failed" class="red--text text--darken-2">
                    {{ $t("login-failure") }}
                  </div>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="12" class="d-flex justify-end">
                  <AdButton
                    type="cancel"
                    color="grey"
                    @click="loginDialog = false; authenticateOnly = false"
                  />
                  <AdButton
                    type="login"
                    noMarginRight
                    :disabled="!loginValid"
                    :loading="validating"
                    submit
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-form>
        </v-card>
      </v-dialog>
      <UserPreferences
        v-if="getCurrentUserDetailsBtn && isUserLoggedIn"
        @cancelUpdateUserPreferences="getCurrentUserDetailsBtn = $event"
        @needCredentials="handleNeedCredentials"
      />
      <v-menu v-if="isUserLoggedIn" offset-y>
        <template v-slot:activator="{ on }">
          <v-btn text v-on="on">
            <v-icon v-if="$vuetify.breakpoint.xs">mdi-account</v-icon>
            <span v-else>{{ currentUsername }}</span>
          </v-btn>
        </template>
        <v-list>
          <v-list-item @click="logout">
            <v-list-item-title>{{ $t("logout") }}</v-list-item-title>
          </v-list-item>
          <v-list-item @click="getCurrentUserDetailsBtn = true">
            <v-list-item-title>{{ $t("user-settings") }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-tooltip content-class="d-block" bottom>
            <template v-slot:activator="{ on: tooltip }">
              <v-btn text v-bind="attrs" v-on="{ ...on, ...tooltip }" class="toolbar-btn">
                <div style="width: 24px; float: left">
                  {{ $i18n.locale.substring(0, 2) }}
                </div>
                <flag
                  :squared="Boolean(0)"
                  :iso="$i18n.locale.substring(3, 5)"
                ></flag>
              </v-btn>
            </template>
            <span>{{ $t('interface-languages')}}</span>
          </v-tooltip>
        </template>
        <v-list>
          <v-list-item
            v-for="(item, index) in $root.langs"
            :key="index"
            @click="$i18n.setLocale(item)"
          >
            <div style="width: 24px; float: left">
              {{ item.substring(0, 2) }}
            </div>
            <flag :squared="Boolean(0)" :iso="item.substring(3, 5)"></flag>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>
    <v-main>
      <v-container fluid>

        <NewsPanel/>
        <TokenExpirationChecker ref="tokenExpirationChecker" @needCredentials="handleNeedCredentials"/>
        <router-view v-if="allImportantPublicDataIsLoaded && (!isUserLoggedIn || allImportantNonPublicDataIsLoaded)" :key="`${$route.params.prefix}` || $route.fullPath"></router-view>

        <!-- if severe problems occur, don't let the user alone -->
        <div v-if="$store.state.server.severeStartupProblems">
          <!-- obviously under maintenance -->
          <v-card width="70%" class="ma-10 redleftborder">
            <v-container class="py-0">
              <v-row dense align-content="center">
                <v-col class="py-0" cols="auto" style="margin-top: 17px">
                  <v-img
                    width="70"
                    :src="require('../src/assets/topmenu-ad-logo2.png')"
                  ></v-img>
                </v-col>
                <v-col class="py-0">
                  <v-card-text>
                    <div v-html="$t('severe-startup-problem-hint')"> </div>
                    <div class="pt-3 pb-3">
                      <v-icon class="mr-2">mdi-email-check-outline </v-icon>
                      <span v-html="$t('support-startproblem', { url: $root.mailtoPrefix + $root.supportAddress })"/>
                    </div>
                  </v-card-text
                  >
                </v-col>
              </v-row>
            </v-container>
          </v-card>
        </div>

      </v-container>
    </v-main>

    <!-- display api loading spinner-->
    <AdApiLoadingSpinner />

    <v-footer inset :color="$vuetify.theme.themes.light.topbottomstrip" app>
      <span class="grey--text pr-2" style="font-size: small"
        >&copy; 2020-{{ new Date().getFullYear() }} ART-DECOR®</span
      >
      <v-icon color="green lighten-2">mdi-vuejs</v-icon>
      <v-icon color="blue lighten-2">mdi-vuetify</v-icon>
      <v-img max-width="60" :src="require('../src/assets/existdb.png')"></v-img>
    </v-footer>
  </v-app>
</template>

<script>

import Vue from 'vue'

import AuthenticationService from '@/services/AuthenticationService'
import ProjectPicker from '@/components/Project/ProjectPicker'
import UserPreferences from '@/components/ServerAdmin/UserPreferences'
import MessageSnackbar from '@/components/Utilities/MessageSnackbar'
import AdAlertConfirm from '@/components/Utilities/AdAlertConfirm'
import NewsPanel from '@/components/Utilities/NewsPanel'
import TokenExpirationChecker from '@/components/Utilities/TokenExpirationChecker'
import rulesUser from '@/rules/user'
import { mapGetters } from 'vuex'
import conceptMapListeners from '@/listeners/conceptMap'
import datasetListeners from '@/listeners/dataset'
import issueListeners from '@/listeners/issue'
import projectListeners from '@/listeners/project'
import serverListeners from '@/listeners/server'
import scenarioListeners from '@/listeners/scenario'
import transactionListeners from '@/listeners/transaction'
import actorListeners from '@/listeners/actor'
import valuesetListeners from '@/listeners/valueset'
import codesystemListeners from '@/listeners/codesystem'
import templateListeners from '@/listeners/template'
import profileListeners from '@/listeners/profile'
import implementationGuideListeners from '@/listeners/implementationGuide'
import questionnaireListeners from '@/listeners/questionnaire'

/**
 * Main app component.
 */
export default {
  name: 'App',
  props: {
    source: String
  },
  components: {
    UserPreferences,
    MessageSnackbar,
    AdAlertConfirm,
    NewsPanel,
    ProjectPicker,
    TokenExpirationChecker
  },
  data () {
    return {
      authenticateOnly: false,
      loginForm: false,
      loginValid: false,
      validating: false,
      rulesUser,
      userPreferencesForm: false,
      username: '',
      password: '',
      error: null,
      failed: false,
      getCurrentUserDetailsBtn: false,
      show1: false,
      changePasswordArea: false,
      drawer: null
    }
  },
  computed: {
    ...mapGetters({
      menuItemsIfProjectSelected: ['app/menuItemsIfProjectSelectedFiltered'],
      menuItemsNoProjectSelected: ['app/menuItemsNoProjectSelectedFiltered'],
      menuItemsServerAdmin: ['app/menuItemsServerAdminFiltered'],
      menuItemsDesign: ['app/menuItemsDesignFiltered'],
      projectLanguage: ['app/projectLanguage']
    }),
    ...mapGetters('project/scheduledTasks', ['questionnaireTransformTasks']),

    loginDialog: {
      get () {
        return this.$store.getters['authentication/loginDialog']
      },
      set (status) {
        if (status === false) {
          this.username = ''
          this.password = ''
        }
        this.$store.dispatch('authentication/setLoginDialog', status)
      }
    },
    allImportantPublicDataIsLoaded () {
      return (
        !this.$store.state.server.serverInfoLoading &&
        !this.$store.state.server.decorTypesLoading &&
        !this.$store.state.server.governancegroupsLoading
      )
    },
    allImportantNonPublicDataIsLoaded () {
      return !this.$store.state.server.decorStatusMapsLoading
    },
    currentProjectPrefix () {
      return this.projectInFocus?.prefix || null
    },
    currentUsername () {
      return this.$store.getters['authentication/getUsername']
    },
    currentUser () {
      return JSON.parse(
        JSON.stringify(this.$store.getters['authentication/getUser'])
      )
    },
    conceptMapItemsLoaded () {
      return this.$store.state.terminology.loaded.conceptMapItems
    },
    datasetItemsLoaded () {
      return this.$store.state.dataset.loaded.datasetItems
    },
    questionnaireItemsLoaded () {
      return this.$store.state.questionnaire.loaded.questionnaires
    },
    scenarioItemsLoaded () {
      return this.$store.state.scenario.loaded.scenarioItems
    },
    actorsLoaded () {
      return this.$store.state.scenario.loaded.actors
    },
    valuesetItemsLoaded () {
      return this.$store.state.terminology.loaded.valueSet
    },
    codesystemItemsLoaded () {
      return this.$store.state.terminology.loaded.codeSystemItems
    },
    templateItemsLoaded () {
      return this.$store.state.rule.template.loaded.templates
    },
    profileItemsLoaded () {
      return this.$store.state.rule.profile.loaded.profiles
    },
    implementationGuideItemsLoaded () {
      return this.$store.state.project.ig.loaded.implementationGuides
    },
    majminversion () {
      const a = this.$root.appversion.split('-')
      if (a.length >= 1) return a[0]
      return 'x.0'
    }
  },
  async created () {
    // Check if user is logged in.
    if (this.isUserLoggedIn === true) {
      // Check if user is authenticated.
      await AuthenticationService.test()
        .then(() => {
          const username = this.$store.getters['authentication/getUsername']
          if (username) {
            this.$store.dispatch('authentication/getUserInfo', username)
              .catch(error => {
                const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
                this.$oops(errorMessage)
              })
          }
        })
        .catch(() => {
        // Force user logout.
          this.$store.dispatch('authentication/clearTokenData')
          // Check if window location reload is a function.
          if (window?.location?.reload instanceof Function) {
          // Perform browser reload.
            window.location.reload()
          }
        })
      // Change user language to user preferred language
      const userDefaultLanguage = this.$store.getters['authentication/getUserDefaultLanguage']
      if (userDefaultLanguage) {
        this.$i18n.setLocale(userDefaultLanguage)
      }
    }

    // Get decor types and other relevant data to make the app work
    // Dispatch decor types to store.
    await this.$store.dispatch('server/setServerFunctions')
      .catch((error) => {
        this.$notify('Server Function Registry not found or unavailable.')
        this.$oops(error.message)
      })
    const setSetServerInfoPromise = this.$store.dispatch('server/setServerInfo')
    const setDecorTypesPromise = this.$store.dispatch('server/setDecorTypes')
    const setGovernancegroupsPromise = this.$store.dispatch('server/setGovernancegroups')
    const setDecorStatusMapsPromise = this.$store.dispatch('server/setDecorStatusMaps')
    const setServerSettingsPromise = this.$store.dispatch('server/setServerSettings')
    const setProjectListPromise = this.$store.dispatch('project/setProjects')

    Promise.all([setSetServerInfoPromise, setDecorTypesPromise, setGovernancegroupsPromise, setDecorStatusMapsPromise, setServerSettingsPromise, setProjectListPromise])
      .then(() => {
        // Listen for general update events
        this.sockets.subscribe('POST/project', projectListeners.postProject.bind(this, { silent: false }))
        this.sockets.subscribe('POST/governancegroup', serverListeners.postGovernanceGroup.bind(this, { silent: false }))
        this.sockets.subscribe('PATCH/governancegroup', serverListeners.patchGovernanceGroup.bind(this, { silent: false }))
        this.sockets.subscribe('DELETE/governancegroup', serverListeners.deleteGovernanceGroup.bind(this, { silent: false }))
        this.sockets.subscribe('server-info-updated', serverListeners.updateServerInfo.bind(this, { silent: true }))
        this.sockets.subscribe('server-functions-updated', serverListeners.updateServerFunctions.bind(this, { silent: true }))
        this.sockets.subscribe('server-scheduled-tasks-updated', (data) => {
          this.$store.commit('server/setServerScheduledTasks', data)
        })
        /**
         * Debug via localStorage
         */
        const debugMode = localStorage.getItem('debug')
        if (debugMode) {
          this.$store.dispatch('app/changeDebugMode', (debugMode === '1'))
        } else {
          if (this.$store.getters['app/debugMode']) {
            this.$store.dispatch('app/changeDebugMode', false)
          }
        }
      })
      .catch((error) => {
        this.$notify('Failed in getting basic server info.', 'error', { stay: true })
        // this is a severe error, signal it to the store
        this.$store.commit('server/setServerStartupProblems', true)
        this.$oops(this.$t('server-info-get-failed', { errmsg: error.message }))
      })
  },
  mounted () {
    // confirm, alert dialogs and notifier snackbar preps
    Vue.prototype.$alert = this.$refs.alert.alert
    Vue.prototype.$oops = this.$refs.alert.oops
    Vue.prototype.$confirm = this.$refs.confirm.confirm
    Vue.prototype.$notify = this.$refs.notify.open
  },
  methods: {
    async login () {
      this.validating = false
      this.failed = false
      this.loginDialog = true
      this.$nextTick(() => {
        if (this.$refs.loginForm) {
          this.$refs.loginForm.resetValidation()
        }
      })
    },
    async validate () {
      if (this.$refs.loginForm.validate()) {
        this.validating = true
        if (this.username.length > 0) {
          try {
            const response = await AuthenticationService.login({
              username: this.username,
              password: this.password
            })

            this.$store.dispatch('authentication/setTokenData', response.data)

            if (this.$refs.tokenExpirationChecker) {
              this.$refs.tokenExpirationChecker.checkTokenExpiration()
            }

            if (!this.authenticateOnly) {
              // Refetch the project list
              this.$store.dispatch('project/setProjects').catch(error => {
                const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
                this.$oops(errorMessage)
              })

              // Fetch user details
              this.$store.dispatch('authentication/getUserInfo', this.username)
                .catch(error => {
                  const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
                  this.$oops(errorMessage)
                })

              // Fetch the authors list
              if (this.projectInFocus?.prefix) {
                await this.$store.dispatch('project/getProjectAuthorList', { prefix: this.projectInFocus.prefix })
                  .catch(error => {
                    const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
                    this.$oops(errorMessage)
                  })
              }

              // Change user language to user preferred language
              const userDefaultLanguage = response.data?.user?.defaultLanguage
              if (userDefaultLanguage) {
                this.$i18n.setLocale(userDefaultLanguage)
                // Change project language if project is selected and project has userDefaultLanguage
                if (this.projectLocales?.length > 1 && this.projectLocales.includes(userDefaultLanguage)) {
                  this.$store.commit('app/setProjectLanguage', userDefaultLanguage)
                }
              }
            }

            this.username = ''
            this.password = ''
            this.failed = false
            // check if the user came from a page that needed authentication
            if (this.$store.state.authentication.to != null) {
              this.$router.push(this.$store.state.authentication.to)
                .then(() => {
                  // reset to property to avoid unlogic redirect after another successfull login
                  this.$store.dispatch('authentication/saveToForRedirect', null)
                })
                .catch(() => {})
            }

            this.loginDialog = false
            this.authenticateOnly = false
          } catch (error) {
            if (error.response) {
              this.error = error.response.data.error
            }
            this.failed = true
          }
        }
      }
      this.validating = false
    },
    async logout () {
      this.$store.dispatch('authentication/clearTokenData')
      this.username = ''
      this.password = ''
      this.$store.commit('authentication/setLoginDialog', false)

      // Filter out private projects
      const publicProjects = this.$store.getters['project/getProjects'].filter(project => project.private !== 'true')
      this.$store.commit('project/setProjects', publicProjects)

      // Clear authors list
      this.$store.commit('project/setAuthors', [])

      // Clear user info
      this.$store.commit('authentication/setUserInfo', null)
    },
    handleNeedCredentials () {
      this.username = this.currentUsername || ''
      this.authenticateOnly = true
      this.$store.commit('authentication/setLoginDialog', true)
    },
    setProjectLanguage (value) {
      this.$store.commit('app/setProjectLanguage', value)
    },
    registerConceptMapListeners () {
      const prefix = this.currentProjectPrefix
      this.sockets.subscribe(`PATCH/${prefix}/conceptMap`, conceptMapListeners.patchConceptMap.bind(this, { silent: true }))
      this.sockets.subscribe(`POST/${prefix}/conceptMap`, conceptMapListeners.postConceptMap.bind(this, { silent: true }))
      this.sockets.subscribe(`PUT/${prefix}/conceptMap`, conceptMapListeners.putConceptMap.bind(this, { silent: true }))
    },
    unregisterConceptMapListeners (prefix) {
      this.sockets.unsubscribe(`PATCH/${prefix}/conceptMap`)
      this.sockets.unsubscribe(`POST/${prefix}/conceptMap`)
      this.sockets.unsubscribe(`PUT/${prefix}/conceptMap`)
    },
    registerDatasetListeners () {
      const prefix = this.currentProjectPrefix
      this.sockets.subscribe(`POST/${prefix}/dataset`, datasetListeners.postDataset.bind(this, { silent: true }))
      this.sockets.subscribe(`POST/${prefix}/datasetStatus`, datasetListeners.postDatasetStatus.bind(this, { silent: true }))
      this.sockets.subscribe(`PUT/${prefix}/dataset`, datasetListeners.putDataset.bind(this, { silent: true }))
      this.sockets.subscribe(`PATCH/${prefix}/dataset`, datasetListeners.patchDataset.bind(this, { silent: true }))
    },
    unregisterDatasetListeners (prefix) {
      this.sockets.unsubscribe(`POST/${prefix}/dataset`)
      this.sockets.unsubscribe(`POST/${prefix}/datasetStatus`)
      this.sockets.unsubscribe(`PUT/${prefix}/dataset`)
      this.sockets.unsubscribe(`PATCH/${prefix}/dataset`)
    },
    registerQuestionnaireListeners () {
      const prefix = this.projectInFocus?.prefix

      this.sockets.subscribe(`PATCH/${prefix}/questionnaire`, questionnaireListeners.patchQuestionnaire.bind(this, { silent: true }))

      this.sockets.subscribe(`GET/${prefix}/scheduled`, (data) => {
        const newQuestionnaireTransformRequests = data['questionnaire-transform-request'] || null
        const currentQuestionnaireTransformRequests = this.$store.getters['project/scheduledTasks/questionnaireTransformTasks']
        const shouldUpdateQuestionnaireTransformRequests = !(newQuestionnaireTransformRequests === null && currentQuestionnaireTransformRequests.length === 0)

        const currentTimestamp = this.$store.getters['project/scheduledTasks/currentTimestamp']
        const newTimestamp = new Date(data.lastModifiedDate).getTime()
        const updateIsObsolete = newTimestamp <= currentTimestamp

        if (!shouldUpdateQuestionnaireTransformRequests || updateIsObsolete) {
          return
        }

        this.$store.dispatch('project/scheduledTasks/setScheduledTasks', data)
      })
    },
    unregisterQuestionnaireListeners () {
      const prefix = this.currentProjectPrefix
      this.sockets.unsubscribe(`GET/${prefix}/scheduled`)
      this.sockets.unsubscribe(`PATCH/${prefix}/questionnaire`)
    },
    registerScenarioListeners () {
      const prefix = this.currentProjectPrefix
      this.sockets.subscribe(`POST/${prefix}/scenario`, scenarioListeners.postScenario.bind(this, { silent: true }))
      this.sockets.subscribe(`PATCH/${prefix}/scenario`, scenarioListeners.patchScenario.bind(this, { silent: true }))
      this.sockets.subscribe(`PUT/${prefix}/scenario`, scenarioListeners.putScenario.bind(this, { silent: true }))
      this.sockets.subscribe(`PATCH/${prefix}/transaction`, transactionListeners.patchTransaction.bind(this, { silent: true }))
      this.sockets.subscribe(`POST/${prefix}/scenarioStatus`, scenarioListeners.postScenarioStatus.bind(this, { silent: true }))
      this.sockets.subscribe(`PUT/${prefix}/transaction`, transactionListeners.putTransaction.bind(this, { silent: true }))
    },
    unregisterScenarioListeners (prefix) {
      this.sockets.unsubscribe(`POST/${prefix}/scenarioStatus`)
      this.sockets.unsubscribe(`PATCH/${prefix}/scenario`)
      this.sockets.unsubscribe(`PATCH/${prefix}/transaction`)
      this.sockets.unsubscribe(`POST/${prefix}/scenario`)
      this.sockets.unsubscribe(`PUT/${prefix}/scenario`)
      this.sockets.unsubscribe(`PUT/${prefix}/transaction`)
    },
    registerActorListeners () {
      const prefix = this.currentProjectPrefix
      this.sockets.subscribe(`POST/${prefix}/scenarioActor`, actorListeners.postScenarioActor.bind(this, { silent: true }))
      this.sockets.subscribe(`PATCH/${prefix}/scenarioActor`, actorListeners.patchScenarioActor.bind(this, { silent: true }))
      this.sockets.subscribe(`DELETE/${prefix}/scenarioActor`, actorListeners.deleteScenarioActor.bind(this, { silent: true }))
    },
    unregisterActorListeners (prefix) {
      this.sockets.unsubscribe(`POST/${prefix}/scenarioActor`)
      this.sockets.unsubscribe(`PATCH/${prefix}/scenarioActor`)
      this.sockets.unsubscribe(`DELETE/${prefix}/scenarioActor`)
    },
    registerValueSetListeners () {
      const prefix = this.currentProjectPrefix
      this.sockets.subscribe(`PATCH/${prefix}/valueset`, valuesetListeners.patchValueSet.bind(this, { silent: true }))
      this.sockets.subscribe(`POST/${prefix}/valueset`, valuesetListeners.postValueSet.bind(this, { silent: true }))
      this.sockets.subscribe(`DELETE/${prefix}/valueset`, valuesetListeners.deleteValueSet.bind(this, { silent: true }))
    },
    unregisterValueSetListeners (prefix) {
      this.sockets.unsubscribe(`PATCH/${prefix}/valueset`)
      this.sockets.unsubscribe(`POST/${prefix}/valueset`)
      this.sockets.unsubscribe(`DELETE/${prefix}/valueset`)
    },
    registerCodeSystemListeners () {
      const prefix = this.currentProjectPrefix
      this.sockets.subscribe(`PATCH/${prefix}/codesystem`, codesystemListeners.patchCodeSystem.bind(this, { silent: true }))
      this.sockets.subscribe(`POST/${prefix}/codesystem`, codesystemListeners.postCodeSystem.bind(this, { silent: true }))
      this.sockets.subscribe(`DELETE/${prefix}/codesystem`, codesystemListeners.deleteCodeSystem.bind(this, { silent: true }))
    },
    unregisterCodeSystemListeners (prefix) {
      this.sockets.unsubscribe(`PATCH/${prefix}/codesystem`)
      this.sockets.unsubscribe(`POST/${prefix}/codesystem`)
      this.sockets.unsubscribe(`DELETE/${prefix}/codesystem`)
    },
    registerTemplateListeners () {
      const prefix = this.currentProjectPrefix
      this.sockets.subscribe(`PATCH/${prefix}/template`, templateListeners.patchTemplate.bind(this, { silent: true }))
      this.sockets.subscribe(`POST/${prefix}/template`, templateListeners.postTemplate.bind(this, { silent: true }))
      this.sockets.subscribe(`PUT/${prefix}/template`, templateListeners.putTemplate.bind(this, { silent: true }))
      this.sockets.subscribe(`DELETE/${prefix}/template`, templateListeners.deleteTemplate.bind(this, { silent: true }))
    },
    unregisterTemplateListeners (prefix) {
      this.sockets.unsubscribe(`PATCH/${prefix}/template`)
      this.sockets.unsubscribe(`POST/${prefix}/template`)
      this.sockets.unsubscribe(`PUT/${prefix}/template`)
      this.sockets.unsubscribe(`DELETE/${prefix}/template`)
    },
    registerProfileListeners () {
      const prefix = this.currentProjectPrefix
      this.sockets.subscribe(`PATCH/${prefix}/profile`, profileListeners.patchProfile.bind(this, { silent: true }))
    },
    unregisterProfileListeners (prefix) {
      this.sockets.unsubscribe(`PATCH/${prefix}/profile`)
    },
    registerImplementationGuideListeners () {
      const prefix = this.currentProjectPrefix
      this.sockets.subscribe(`PATCH/${prefix}/ig`, implementationGuideListeners.patchIG.bind(this, { silent: true }))
      this.sockets.subscribe(`POST/${prefix}/ig`, implementationGuideListeners.postIG.bind(this, { silent: true }))
    },
    unregisterImplementationGuideListeners (prefix) {
      this.sockets.unsubscribe(`PATCH/${prefix}/ig`)
      this.sockets.unsubscribe(`POST/${prefix}/ig`)
    }
  },
  watch: {
    currentProjectPrefix: {
      immediate: true,
      handler (val, oldVal) {
        if (val) {
          this.$socket.emit('scheduled-tasks:join', { prefix: val })

          // Project based event listeners
          this.sockets.subscribe(`POST/${val}/issue`, issueListeners.postIssue.bind(this, { silent: false }))
          this.sockets.subscribe(`PATCH/${val}/issue`, issueListeners.patchIssue.bind(this, { silent: false }))
          this.sockets.subscribe(`projectAuthorsUpdated/${val}`, projectListeners.patchProjectAuthor.bind(this, { silent: false }))
          this.sockets.subscribe(`PATCH/${val}/project`, projectListeners.patchProject.bind(this, { silent: false }))
          this.sockets.subscribe(`PATCH/${val}/project/change-id`, projectListeners.patchChangeProjectArtifactId.bind(this, { silent: false }))
          this.sockets.subscribe(`POST/${val}/issue/label`, issueListeners.postIssueLabel.bind(this, { silent: true }))
          this.sockets.subscribe(`PUT/${val}/issue/label`, issueListeners.putIssueLabel.bind(this, { silent: true }))
          this.sockets.subscribe(`DELETE/${val}/issue/label`, issueListeners.deleteIssueLabel.bind(this, { silent: true }))

          // Get the issue labels, which are needed in several places in the app
          this.$store.dispatch('issues/setLabels').catch(error => {
            const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
            this.$oops(errorMessage)
          })
        }
        if (oldVal) {
          this.$socket.emit('scheduled-tasks:leave', { prefix: oldVal })

          this.sockets.unsubscribe(`POST/${oldVal}/issue`)
          this.sockets.unsubscribe(`PATCH/${oldVal}/issue`)
          this.sockets.unsubscribe(`projectAuthorsUpdated/${oldVal}`)
          this.sockets.unsubscribe(`PATCH/${oldVal}/project`)
          this.sockets.unsubscribe(`PATCH/${oldVal}/project/change-id`)

          this.sockets.unsubscribe(`POST/${oldVal}/issue/label`)
          this.sockets.unsubscribe(`PUT/${oldVal}/issue/label`)
          this.sockets.unsubscribe(`DELETE/${oldVal}/issue/label`)

          this.unregisterConceptMapListeners(oldVal)
          this.unregisterDatasetListeners(oldVal)
          this.unregisterScenarioListeners(oldVal)
          this.unregisterActorListeners(oldVal)
          this.unregisterValueSetListeners(oldVal)
          this.unregisterCodeSystemListeners(oldVal)
          this.unregisterTemplateListeners(oldVal)
          this.unregisterProfileListeners(oldVal)
          this.unregisterImplementationGuideListeners(oldVal)
          this.unregisterQuestionnaireListeners(oldVal)
        }
      }
    },
    conceptMapItemsLoaded (isLoaded) {
      if (isLoaded) {
        this.registerConceptMapListeners()
      }
    },
    datasetItemsLoaded (isLoaded) {
      if (isLoaded) {
        this.registerDatasetListeners()
      }
    },
    questionnaireItemsLoaded (isLoaded) {
      if (isLoaded) {
        this.registerQuestionnaireListeners()
      }
    },
    scenarioItemsLoaded (isLoaded) {
      if (isLoaded) {
        this.registerScenarioListeners()
      }
    },
    actorsLoaded (isLoaded) {
      if (isLoaded) {
        this.registerActorListeners()
      }
    },
    valuesetItemsLoaded (isLoaded) {
      if (isLoaded) {
        this.registerValueSetListeners()
      }
    },
    codesystemItemsLoaded (isLoaded) {
      if (isLoaded) {
        this.registerCodeSystemListeners()
      }
    },
    templateItemsLoaded (isLoaded) {
      if (isLoaded) {
        this.registerTemplateListeners()
      }
    },
    profileItemsLoaded (isLoaded) {
      if (isLoaded) {
        this.registerProfileListeners()
      }
    },
    implementationGuideItemsLoaded (isLoaded) {
      if (isLoaded) {
        this.registerImplementationGuideListeners()
      }
    },
    questionnaireTransformTasks (newVal, oldVal) {
      if (!(newVal.length === 0 && oldVal.length > 0)) {
        return
      }
      if (this.$route.name.includes('questionnaire')) {
        this.$notify(this.$t('new-questionnaire-available'), 'success', { long: true })
      }
      if (this.questionnaireItemsLoaded) {
        this.$store.dispatch('questionnaire/refreshListAndKeepFocus')
      }
    }
  }
}
</script>
<style lang="scss">
@font-face {
  font-family: "Roboto";
  font-weight: 100;
  font-style: normal;
  font-display: auto;
  unicode-range: U+000-5FF;
  src: local("Roboto"), url("@/assets/fonts/Roboto/Roboto-Thin.ttf") format("truetype");
}
@font-face {
  font-family: "Roboto";
  font-weight: 300;
  font-style: normal;
  font-display: auto;
  unicode-range: U+000-5FF;
  src: local("Roboto"), url("@/assets/fonts/Roboto/Roboto-Light.ttf") format("truetype");
}
@font-face {
  font-family: "Roboto";
  font-weight: 400;
  font-style: normal;
  font-display: auto;
  unicode-range: U+000-5FF;
  src: local("Roboto"), url("@/assets/fonts/Roboto/Roboto-Regular.ttf") format("truetype");
}
@font-face {
  font-family: "Roboto";
  font-weight: 500;
  font-style: normal;
  font-display: auto;
  unicode-range: U+000-5FF;
  src: local("Roboto"), url("@/assets/fonts/Roboto/Roboto-Medium.ttf") format("truetype");
}
@font-face {
  font-family: "Roboto";
  font-weight: 700;
  font-style: normal;
  font-display: auto;
  unicode-range: U+000-5FF;
  src: local("Roboto"), url("@/assets/fonts/Roboto/Roboto-Bold.ttf") format("truetype");
}
@font-face {
  font-family: "Roboto";
  font-weight: 900;
  font-style: normal;
  font-display: auto;
  unicode-range: U+000-5FF;
  src: local("Roboto"), url("@/assets/fonts/Roboto/Roboto-Black.ttf") format("truetype");
}

.v-navigation-drawer__content::-webkit-scrollbar {
  display: none;
}
.v-navigation-drawer__content {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.redleftborder {
  border-left: 7px solid rgb(243, 145, 145) !important
}

.toolbar-btn {
  @media screen and (max-width: 600px) {
    min-width: 20px !important;
    padding: 0 !important;
  }
}
</style>
